import React from 'react';
import { UserOutlined } from '@ant-design/icons';
import { Outlet, useMatch, useNavigate, useSearchParams } from 'react-router';
import { Avatar, Layout, Segmented } from 'antd';
import { Locale } from '../context/AppProvider';
import {
  getRecents,
  RECENTS_CHANGED_EVENT,
  type RecentItem,
} from './recentsStorage';
import { useDossierTabs } from './useDossierTabs';
import ClaimPicker from './ClaimPicker/ClaimPicker';
import styles from './Container.module.scss';
import { TAB_META } from './DossierRightPane/DossierRightPane';
import {
  defaultSegment,
  parseSegment,
  SEGMENT_PARAM,
  type SegmentTab,
} from './DossierRightPane/tabs';
import EventsBar from './EventsBar/EventsBar';

const EscContainer: React.FC = () => {
  const { messages } = React.useContext(Locale);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dossierMatch = useMatch('/claims/:dossierId/*');
  const dossierId = dossierMatch?.params.dossierId ?? null;

  const { tabs, ready } = useDossierTabs(dossierId);
  const segment = parseSegment(searchParams, tabs);

  const tabOptions = React.useMemo(
    () =>
      tabs.map((value) => ({
        value,
        label: (
          <span className={styles.segItem}>
            {TAB_META[value].icon}
            <span>
              {messages[TAB_META[value].labelPath] ?? TAB_META[value].fallback}
            </span>
          </span>
        ),
      })),
    [tabs, messages]
  );

  const selectSegment = React.useCallback(
    (value: SegmentTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === defaultSegment(tabs)) {
            next.delete(SEGMENT_PARAM);
          } else {
            next.set(SEGMENT_PARAM, value);
          }
          next.delete('left');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, tabs]
  );

  const [recents, setRecents] = React.useState<RecentItem[]>(getRecents());
  React.useEffect(() => {
    const handler = () => setRecents(getRecents());
    window.addEventListener(RECENTS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(RECENTS_CHANGED_EVENT, handler);
  }, []);

  const activeDossier = dossierId
    ? recents.find((r) => r.dossierId === dossierId)
    : null;

  return (
    <Layout className={styles.shell}>
      <div className={styles.contentWrap}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <ClaimPicker
                view={dossierId ? 'claim' : 'home'}
                activeTitle={activeDossier?.title}
                activeDossierId={dossierId}
                recents={recents}
                onNavigate={navigate}
              />
            </div>
            <div className={styles.headerRight}>
              {dossierId && (
                <>
                  {ready && (
                    <Segmented
                      className={styles.panelSeg}
                      value={segment}
                      onChange={(value) => selectSegment(value as SegmentTab)}
                      options={tabOptions}
                    />
                  )}
                  <EventsBar dossierId={dossierId} />
                </>
              )}
              <Avatar
                size={28}
                icon={<UserOutlined />}
                className={styles.profileAvatar}
              />
            </div>
          </div>
          <div className={styles.body}>
            <Outlet />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EscContainer;
